import Loader from '@/components/common/Loader';
import { shouldUseNopPositions } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountUsages } from '@/state/reducers/accountUsageSlice';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectNopBalance } from '@/state/reducers/balanceSlice';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useNopPositions from '@/utils/hooks/useNopPositions';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { Fragment, forwardRef, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';

interface AccountValueProps {}

export default function AccountValue(props: AccountValueProps) {
    const lang = useLanguage();
    const currentAccount = useAppSelector(selectCurrentAccount);
    const accountUsages = useAppSelector(selectAccountUsages);
    const nopBalances = useAppSelector(selectNopBalance);

    const [stateLoading, setStateLoading] = useState<boolean>(false);

    const nopPositions = useNopPositions({ positionFilter: true });

    const { formattedNopPositions } = nopPositions;

    const { order_decimals } = useMemo(() => {
        if (accountUsages && currentAccount) {
            return getCurrency(accountUsages[currentAccount]?.currency || 'USD');
        } else {
            return getCurrency('USD');
        }
    }, [accountUsages, currentAccount]);

    const formatNumber = useNumberFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: Math.max(0, order_decimals - 2)
    });

    const usages = useMemo(() => {
        if (accountUsages && currentAccount) {
            const accountUsage = accountUsages[currentAccount];
            if (accountUsage) {
                return {
                    currency: accountUsage.currency || 'USD',
                    unrealizedPnL: BigNumber(accountUsage.unrealisedPnl).toNumber(),
                    totalEquity: BigNumber(accountUsage.accountValue).toNumber(),
                    marginUsed: BigNumber(accountUsage.usage).toNumber(),
                    availableMargin: BigNumber(accountUsage.accountValue).minus(accountUsage.usage).toNumber(),
                    marginUsage: BigNumber(accountUsage.usage)
                        .dividedBy(accountUsage.accountValue || 1)
                        .times(100)
                        .toNumber()
                };
            }
        }

        return null;
    }, [accountUsages, currentAccount]);

    const nopUsages = useMemo(() => {
        if (formattedNopPositions) {
            if (accountUsages && currentAccount && shouldUseNopPositions) {
                const unrealizedPnL = formattedNopPositions.map(
                    (position) => position.unrealisedPnlInBaseCurrency.value
                );

                const unrealizedPnlInBaseCurrency =
                    unrealizedPnL.reduce((acc, position) => {
                        return BigNumber(acc || 0)
                            .plus(position || 0)
                            .toNumber();
                    }, 0) || 0;

                const accountUsage = accountUsages[currentAccount];
                if (accountUsage) {
                    const totalBalances = nopBalances.balances.reduce((acc, balance) => {
                        return BigNumber(acc)
                            .plus(balance.totalAmountInBaseCurrency || 0)
                            .toNumber();
                    }, 0);

                    const totalEquity = BigNumber(totalBalances)
                        .plus(unrealizedPnlInBaseCurrency || 0)
                        .toNumber();
                    const marginUsed = BigNumber(accountUsage.usage).toNumber();
                    const availableMargin = BigNumber(totalEquity).minus(marginUsed).toNumber();
                    return {
                        currency: accountUsage.currency || 'USD',
                        unrealizedPnL: unrealizedPnlInBaseCurrency,
                        cashBalance: totalBalances,
                        totalEquity,
                        marginUsed,
                        availableMargin,
                        marginUsage: BigNumber(marginUsed)
                            .dividedBy(totalEquity || 1) // Avoid division by zero
                            .times(100)
                            .toNumber()
                    };
                }
            }
        }

        return null;
    }, [formattedNopPositions, nopBalances, accountUsages, currentAccount]);

    useDidUpdate(() => {
        setStateLoading(true);
        setTimeout(() => setStateLoading(false), 1000);
    }, [currentAccount]);

    return (
        <div className="flex flex-col w-full h-full text-brand-text overflow-y-auto py-2 gap-2">
            {stateLoading ? (
                <div className="m-2 mt-0">
                    <Loader />
                </div>
            ) : (
                <Fragment>
                    {usages && !shouldUseNopPositions && (
                        <Fragment>
                            <Row
                                usages={usages}
                                label={lang.accountValue.unrealizedPL}
                                accessorKey="unrealizedPnL"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={usages}
                                label={lang.accountValue.equity}
                                accessorKey="totalEquity"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={usages}
                                label={lang.accountValue.marginUsed}
                                accessorKey="marginUsed"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={usages}
                                label={lang.accountValue.availableMargin}
                                accessorKey="availableMargin"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={usages}
                                label={lang.accountValue.marginUsage}
                                accessorKey="marginUsage"
                                formatNumber={formatNumber}
                            />
                        </Fragment>
                    )}
                    {nopUsages && shouldUseNopPositions && (
                        <Fragment>
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.cashBalance}
                                accessorKey="cashBalance"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.unrealizedPL}
                                accessorKey="unrealizedPnL"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.equity}
                                accessorKey="totalEquity"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.marginUsed}
                                accessorKey="marginUsed"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.availableMargin}
                                accessorKey="availableMargin"
                                formatNumber={formatNumber}
                            />
                            <Row
                                usages={nopUsages}
                                label={lang.accountValue.marginUsage}
                                accessorKey="marginUsage"
                                formatNumber={formatNumber}
                            />
                        </Fragment>
                    )}
                </Fragment>
            )}
        </div>
    );
}

interface RowProps {
    label: string;
    usages: {
        currency: string;
        unrealizedPnL: number;
        totalEquity: number;
        marginUsed: number;
        availableMargin: number;
        marginUsage: number;
    };
    accessorKey: 'cashBalance' | 'unrealizedPnL' | 'totalEquity' | 'marginUsed' | 'availableMargin' | 'marginUsage';
    formatNumber: Intl.NumberFormat;
}

const Row = forwardRef<HTMLInputElement, RowProps>(({ label, usages, accessorKey, formatNumber }, ref) => {
    const value = usages[accessorKey];

    const balanceColor = useMemo(() => {
        if (value === 0 || accessorKey === 'marginUsage') return 'text-brand-text';
        else if (value > 0) return 'text-brand-green';
        else return 'text-brand-red';
    }, [value]);

    const displayValue = useMemo(() => {
        if (accessorKey === 'marginUsage') {
            if (value < 0) return '∞';
            else if (value > 100) return formatNumber.format(value);
            else return formatNumber.format(value);
        }
        return formatNumber.format(value);
    }, [value, usages]);

    return (
        <div ref={ref} className="w-full text-sm flex justify-between items-center gap-2 px-2 whitespace-nowrap">
            <span>{label}</span>
            <div className={cn('flex flex-wrap-reverse justify-end gap-x-2', balanceColor)}>
                {accessorKey === 'marginUsage' && <ProgressBar valuePercentage={value} />}
                {accessorKey !== 'marginUsage' && usages.currency} {displayValue}{' '}
                {accessorKey === 'marginUsage' && value >= 0 && '%'}
            </div>
        </div>
    );
});

Row.displayName = 'Row';

type ProgressBarProps = {
    valuePercentage: number;
};

const ProgressBar = (props: ProgressBarProps) => {
    const { valuePercentage: value } = props;
    const fillerRelativePercentage = (100 / value) * 100;

    return (
        <div
            className="w-16 flex items-center"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={value}>
            <div
                className="flex-1 h-2 rounded-full bg-brand-neutral-200 border-md overflow-hidden"
                style={{ width: `${value}%` }}>
                <div className="filler" style={{ width: `${value}%` }}>
                    <div className="filler-background" style={{ width: `${fillerRelativePercentage}%` }} />
                </div>
            </div>
        </div>
    );
};
